import { Component, Input, OnInit } from '@angular/core';
import { ApplyColumnStateParams, ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { AgGridColumns, AgGridFilterParams, AgGridOptionsBuilder } from '../../Compliance/AgGrid';
import { AgGridYesNoFloatingFilterComponent } from '../../Compliance/AgGrid/FloatingFilters/agGridYesNoFloatingFilter.component';
import { WeissmanModalService } from '../../Compliance/WeissmanModalService';
import { RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import {
    EqualizationRuleModalComponent,
    EqualizationRuleModalParams,
    EqualizationRuleModalResult
} from './equalization.rule.modal.component';
import { EqualizationRuleService } from './equalization.rule.service';
import {
    EqualizationRuleListGridActionCellRendererComponent,
    ICellRendererParamsForEqualizationRuleGridAction
} from './agGridActionCellRenderer.component';

@Component({
    selector: 'equalization-rules-panel',
    templateUrl: './equalization.rules.panel.component.html',
    standalone: false
})
export class EqualizationRulesPanelComponent implements OnInit {
    constructor(
        private readonly _restrictService: RestrictService,
        private readonly _modalService: WeissmanModalService,
        private readonly _equalizationRuleService: EqualizationRuleService,
        private readonly _toastr: ToastrService
    ) {}

    @Input() assessorId: number;
    gridId: System.Guid = '7C8E1F3A-9B2D-4E6C-8F1A-3D5B6E7C8A9D';
    hasEditPermission: boolean = false;
    showBody: boolean = false;
    gridApi: GridApi;
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withContext(this)
        .withColumnPinning()
        .withSort()
        .withLoadingOverlay()
        .withColumnResize()
        .build();

    ngOnInit(): void {
        this.hasEditPermission = this._restrictService.isInRole(Roles.JURISDICTIONSETUPS);
    }

    expandOrCollapse(): void {
        this.showBody = !this.showBody;
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;
        this.gridApi.setGridOption('columnDefs', this._getColumnDefinitions());
        this.gridApi.applyColumnState(this._getDefaultSort());
        this.gridApi.sizeColumnsToFit();
        void this._loadRules();
    }

    async newEqualizationRule(): Promise<void> {
        const result = await this._modalService.showAsync<EqualizationRuleModalParams, EqualizationRuleModalResult>(
            EqualizationRuleModalComponent,
            { assessorId: this.assessorId },
            'modal-md'
        );

        if (result?.hasChanges) {
            await this._loadRules();
        }
    }

    private async _viewEqualizationRule(params: ICellRendererParamsForEqualizationRuleGridAction): Promise<void> {
        const row = params.data as Core.EqualizationRuleModel;

        const result = await this._modalService.showAsync<EqualizationRuleModalParams, EqualizationRuleModalResult>(
            EqualizationRuleModalComponent,
            { assessorId: this.assessorId, equalizationRule: row },
            'modal-md'
        );

        if (result?.isDeleted || result?.hasChanges) {
            await this._loadRules();
        }
    }

    private async _loadRules(): Promise<void> {
        try {
            const rules = await this._equalizationRuleService.getByAssessor(this.assessorId);
            this.gridApi.setGridOption('rowData', rules);
            this.gridApi.sizeColumnsToFit();
        } catch {
            this._toastr.error('Unable to load Equalization Rules.');
        }
    }

    private _getColumnDefinitions(): ColDef[] {
        return [
            {
                headerName: 'Property Type',
                field: 'propertyType',
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                comparator: (valueA, valueB, nodeA, nodeB) =>
                    nodeA.data.propertyTypeSequence - nodeB.data.propertyTypeSequence
            },
            {
                headerName: 'Tax Year',
                field: 'taxYear',
                flex: 1,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterParamsWithEqualTo,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams
            },
            {
                headerName: 'Eq. Factor',
                field: 'factor',
                flex: 1,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithEqualToAndBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                valueFormatter: params => params.value === null || params.value === undefined
                    ? ''
                    : Number(params.value).toFixed(4)
            },
            {
                headerName: 'Status',
                field: 'isActual',
                flex: 1,
                cellDataType: false,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                filterValueGetter: params => params.data.isActual ? 'Actual' : 'Estimate',
                valueFormatter: params => params.value ? 'Actual' : 'Estimate'
            },
            {
                headerName: 'Automatically Add',
                field: 'automaticallyAdd',
                flex: 1,
                cellDataType: false,
                filter: 'agYesNoColumnFilter',
                filterParams: AgGridFilterParams.yesNoFilterParams,
                floatingFilterComponent: AgGridYesNoFloatingFilterComponent,
                valueFormatter: params => params.value ? 'Yes' : 'No'
            },
            {
                colId: 'grid-column-actions',
                headerName: '',
                field: 'actions',
                width: AgGridColumns.getActionColumnWidth(1),
                minWidth: AgGridColumns.getActionColumnWidth(1),
                maxWidth: AgGridColumns.getActionColumnWidth(1),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                pinned: 'right',
                lockVisible: true,
                sortable: false,
                cellRenderer: EqualizationRuleListGridActionCellRendererComponent,
                cellRendererParams: {
                    viewDetails: this._viewEqualizationRule.bind(this)
                } as ICellRendererParamsForEqualizationRuleGridAction
            }
        ];
    }

    private _getDefaultSort(): ApplyColumnStateParams {
        return {
            state: [
                { colId: 'propertyType', sort: 'asc', sortIndex: 0 },
                { colId: 'taxYear', sort: 'desc', sortIndex: 1 }
            ],
            defaultState: { sort: null }
        };
    }
}
